import fleetnexisLogo from '../../../assets/images/logos/fleetnexis-logo.png';

// Shared FleetNexis document layout for PDFs built in the panel (quotations,
// store sale invoices). Mirrors the mobile API booking receipt
// (fleetnexis-mobile-api generateReceipt) so every customer document has one look.

export const COLORS = {
  accent: '#0284C7',
  ink: '#0F172A',
  slate800: '#1E293B',
  slate700: '#334155',
  slate600: '#475569',
  muted: '#64748B',
  faint: '#94A3B8',
  border: '#E2E8F0',
  panel: '#F8FAFC',
  totalLabel: '#93C5FD',
};

export const BADGES = {
  success: { color: '#047857', fill: '#D1FAE5' },
  warning: { color: '#B45309', fill: '#FEF3C7' },
  info: { color: '#0369A1', fill: '#E0F2FE' },
  neutral: { color: '#475569', fill: '#F1F5F9' },
};

const PAGE_WIDTH = 523; // A4 width minus 36pt side margins

export const TRN = '104138666300003';

export const TERMS = [
  'Warranty cover bad installment and heat rejection rate.',
  'Ceramics should be maintained every 6 months.',
  'In the event that the car is washed outside Fleetnexis, the ceramic guarantee is void.',
  'The warranty for the PPF is for the factory paint only, and it is considered void if the car was painted.',
  'The PPF should only be removed By Fleetnexis to ensure that the paint is not damaged.',
  'Car Wash is not responsible for damages on installation and removal of the PPF if the vehicle was painted',
  'For battery warranty card must be presented, otherwise the warranty will be considered void',
];

export const formatAmount = (amount) => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '0.00';
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const isBlank = (value) => {
  const v = String(value ?? '').trim().toLowerCase();
  return !v || ['string', 'null', 'undefined', '-', 'none', '0', 'default'].includes(v);
};

// First value that holds real data (skips '', null, 'string', '-', ...).
export const firstPresent = (...values) => values.find((v) => !isBlank(v));

// Name of the logged-in panel user, for the signature block.
export const signedInUserName = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}')?.user_retrive_name || '';
  } catch (err) {
    return '';
  }
};

// pdfMake (0.2.x) drops the spaces between Arabic words and shows them in the
// wrong order. For text with Arabic and no Latin letters, reversing the words
// and joining them with no-break spaces prints it as it reads.
export const rtlText = (value) => {
  const text = String(value ?? '').trim();
  if (!/[\u0600-\u06FF]/.test(text) || /[a-z]/i.test(text)) return text;
  return text.split(/\s+/).reverse().join('\u00A0');
};

export const titleCase = (value) =>
  isBlank(value) ? '-' : String(value).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const formatVehicleType = (carType) => {
  const ct = String(carType ?? '').trim();
  const lower = ct.toLowerCase();
  if (isBlank(ct)) return 'Standard Vehicle';
  if (['salon', 'saloon', 'sedan'].includes(lower)) return 'Sedan / Salon';
  if (['four-wheeler', 'four_wheeler', 'four wheeler', 'suv', '4x4', '4-wheeler'].includes(lower)) {
    return 'SUV / 4-Wheeler';
  }
  if (lower === 'hatchback') return 'Hatchback';
  if (lower === 'coupe') return 'Coupe / Sports';
  if (lower === 'pickup' || lower === 'truck') return 'Pickup / Truck';
  if (lower === 'van' || lower === 'minivan') return 'Van / Minivan';
  return titleCase(ct);
};

export const mapPaymentMode = (mode) => {
  const m = String(mode ?? '').toLowerCase().trim();
  if (m === 'paid_card') return 'Fleet Nexis Card';
  if (m === 'bank_card' || m === 'card') return 'Credit / Debit Card';
  if (m === 'bank_transfer' || m === 'transfer') return 'Bank Transfer';
  if (m === 'razorpay') return 'Online (Razorpay)';
  if (m === 'cash') return 'Cash';
  if (m === 'apple_pay') return 'Apple Pay';
  if (m === 'wallet') return 'FleetNexis Wallet';
  if (m === 'link') return 'Payment Link';
  if (!m || m === '-') return '-';
  return titleCase(m);
};

const label = (text, color = COLORS.accent) => ({
  text,
  fontSize: 7.5,
  bold: true,
  color,
  characterSpacing: 0.5,
});

// One "Key: value" line inside a card.
export const infoRow = (key, value, { width = 75, bold = false, color = COLORS.slate700 } = {}) => ({
  columns: [
    { text: key, fontSize: 8.5, color: COLORS.muted, width },
    { text: value, fontSize: 8.5, bold, color },
  ],
  margin: [0, 0, 0, 1],
});

const card = (title, stack, titleColor) => ({
  table: {
    widths: ['*'],
    body: [
      [
        {
          fillColor: COLORS.panel,
          border: [true, true, true, true],
          borderColor: [COLORS.border, COLORS.border, COLORS.border, COLORS.border],
          margin: [8, 8, 8, 8],
          stack: [label(title, titleColor), { stack, margin: [0, 3, 0, 0] }],
        },
      ],
    ],
  },
});

const itemsTable = ({ indexHeader = '#', middleHeader, lines, emptyMessage, currency }) => {
  const body = [
    [
      { text: indexHeader, style: 'tableHeader', alignment: 'center' },
      { text: 'DESCRIPTION', style: 'tableHeader' },
      { text: middleHeader, style: 'tableHeader', alignment: 'center' },
      { text: 'AMOUNT', style: 'tableHeader', alignment: 'right' },
    ],
  ];

  if (lines.length === 0) {
    body.push([
      { text: emptyMessage, colSpan: 4, alignment: 'center', color: COLORS.muted, style: 'tableCell' },
      {},
      {},
      {},
    ]);
  }

  lines.forEach((line, i) => {
    const rowBg = i % 2 === 0 ? '#FFFFFF' : COLORS.panel;
    body.push([
      { text: String(i + 1), style: 'tableCell', alignment: 'center', fillColor: rowBg },
      {
        stack: [
          { text: line.name, bold: true, color: COLORS.ink, fontSize: 9.5 },
          ...(line.subtitle
            ? [{ text: line.subtitle, color: COLORS.muted, fontSize: 8, margin: [0, 2, 0, 0] }]
            : []),
        ],
        style: 'tableCell',
        fillColor: rowBg,
      },
      {
        text: line.middle,
        style: 'tableCell',
        alignment: 'center',
        color: COLORS.slate600,
        fontSize: 8.5,
        fillColor: rowBg,
      },
      {
        text: `${formatAmount(line.amount)} ${currency}`,
        style: 'tableCell',
        alignment: 'right',
        bold: true,
        color: COLORS.ink,
        fillColor: rowBg,
      },
    ]);
  });

  return {
    // A worded first header ("SERIAL NO.") needs more room than "#".
    table: { headerRows: 1, widths: [indexHeader === '#' ? 22 : 54, '*', 110, 85], body },
    layout: {
      hLineWidth: (i, node) => (i === 0 || i === 1 || i === node.table.body.length ? 1 : 0.5),
      vLineWidth: () => 0,
      hLineColor: (i) => (i === 1 ? COLORS.ink : COLORS.border),
      paddingLeft: () => 8,
      paddingRight: () => 8,
      paddingTop: () => 7,
      paddingBottom: () => 7,
    },
    margin: [0, 0, 0, 14],
  };
};

/**
 * Builds a pdfMake document definition in the FleetNexis receipt layout.
 *
 * lines: [{ name, subtitle?, middle, amount }] — amount is pre-tax; `middle`
 * fills the column headed `middleHeader` (vehicle, qty x rate, ...);
 * `indexHeader` heads the row-number column ('#' unless given). `discount`
 * (optional, pre-tax) is taken off the subtotal before tax.
 * Cards: { title, rows } where rows are pdfMake nodes (see infoRow).
 */
export const buildBrandedPdf = ({
  logo,
  title,
  badge,
  reference,
  issued,
  customerCard,
  detailsCard,
  paymentCard,
  indexHeader,
  middleHeader,
  lines,
  emptyMessage,
  currency,
  taxLineItems,
  getAmountWithTax,
  discount = 0,
  totalLabel,
  signatureName,
  supportText,
  footerText,
}) => {
  const subtotal = lines.reduce((sum, line) => sum + line.amount, 0);
  const discountAmount = Math.min(Math.max(Number(discount) || 0, 0), subtotal);
  const taxable = subtotal - discountAmount;
  const total = getAmountWithTax(taxable);

  return {
    pageSize: 'A4',
    pageMargins: [36, 24, 36, 36],
    footer: (currentPage, pageCount) => ({
      margin: [36, 0, 36, 0],
      stack: [
        { canvas: [{ type: 'rect', x: 0, y: 0, w: PAGE_WIDTH, h: 2, color: COLORS.accent }] },
        {
          columns: [
            { text: footerText, fontSize: 7.5, color: COLORS.faint, margin: [0, 6, 0, 0] },
            {
              text: `Page ${currentPage} of ${pageCount}`,
              fontSize: 7.5,
              color: COLORS.faint,
              alignment: 'right',
              margin: [0, 6, 0, 0],
            },
          ],
        },
      ],
    }),
    content: [
      // Top accent bar
      {
        canvas: [{ type: 'rect', x: 0, y: 0, w: PAGE_WIDTH, h: 3.5, color: COLORS.accent }],
        margin: [0, 0, 0, 12],
      },

      // Header: logo | document title, badge, reference
      {
        columns: [
          {
            width: '*',
            stack: [
              logo
                ? { image: logo, width: 145 }
                : { text: 'FleetNexis', fontSize: 22, bold: true, color: COLORS.ink },
              {
                text: 'PREMIUM CAR CARE & FLEET SERVICES',
                fontSize: 7.5,
                bold: true,
                color: COLORS.accent,
                characterSpacing: 0.8,
                margin: [0, 5, 0, 0],
              },
              {
                text: 'Certified Eco-Friendly Steam Wash & Mobile Detailing',
                fontSize: 7.5,
                color: COLORS.muted,
                margin: [0, 1, 0, 0],
              },
            ],
          },
          {
            width: 'auto',
            alignment: 'right',
            stack: [
              {
                text: title,
                fontSize: 18,
                bold: true,
                color: COLORS.ink,
                characterSpacing: 0.5,
                alignment: 'right',
              },
              {
                columns: [
                  { width: '*', text: '' },
                  {
                    width: 'auto',
                    table: {
                      widths: ['auto'],
                      body: [
                        [
                          {
                            text: badge.text,
                            noWrap: true,
                            fontSize: 8,
                            bold: true,
                            color: badge.color,
                            fillColor: badge.fill,
                            margin: [10, 3, 10, 3],
                            alignment: 'center',
                          },
                        ],
                      ],
                    },
                    layout: 'noBorders',
                  },
                ],
                margin: [0, 4, 0, 5],
              },
              { text: reference, fontSize: 8.5, bold: true, color: COLORS.slate800, alignment: 'right' },
              { text: `Issued: ${issued}`, fontSize: 8, color: COLORS.muted, alignment: 'right', margin: [0, 1, 0, 0] },
              { text: `TRN: ${TRN}`, fontSize: 8, color: COLORS.muted, alignment: 'right', margin: [0, 1, 0, 0] },
            ],
          },
        ],
        margin: [0, 0, 0, 12],
      },

      // Divider
      {
        canvas: [
          { type: 'line', x1: 0, y1: 0, x2: PAGE_WIDTH, y2: 0, lineWidth: 0.5, lineColor: COLORS.border },
        ],
        margin: [0, 0, 0, 12],
      },

      // Customer | document details
      {
        columns: [
          { width: '*', ...card(customerCard.title, customerCard.rows) },
          { width: '*', ...card(detailsCard.title, detailsCard.rows) },
        ],
        columnGap: 12,
        margin: [0, 0, 0, 14],
      },

      itemsTable({ indexHeader, middleHeader, lines, emptyMessage, currency }),

      // Payment (left) | financial breakdown (right)
      {
        columns: [
          { width: 250, ...card(paymentCard.title, paymentCard.rows, COLORS.muted) },
          {
            width: '*',
            stack: [
              {
                table: {
                  widths: ['*', 'auto'],
                  body: [
                    [
                      { text: 'Net Amount / Subtotal', fontSize: 9, color: COLORS.slate600, margin: [0, 2, 0, 2] },
                      {
                        text: `${formatAmount(subtotal)} ${currency}`,
                        fontSize: 9.5,
                        bold: true,
                        color: COLORS.ink,
                        alignment: 'right',
                        margin: [0, 2, 0, 2],
                      },
                    ],
                    ...(discountAmount > 0
                      ? [[
                        { text: 'Discount', fontSize: 8.5, color: COLORS.muted, margin: [0, 2, 0, 2] },
                        {
                          text: `- ${formatAmount(discountAmount)} ${currency}`,
                          fontSize: 8.5,
                          color: COLORS.slate600,
                          alignment: 'right',
                          margin: [0, 2, 0, 2],
                        },
                      ]]
                      : []),
                    ...taxLineItems.map((tax) => [
                      { text: tax.label, fontSize: 8.5, color: COLORS.muted, margin: [0, 2, 0, 2] },
                      {
                        text: `${formatAmount(taxable * Number(tax.rate || 0))} ${currency}`,
                        fontSize: 8.5,
                        color: COLORS.slate600,
                        alignment: 'right',
                        margin: [0, 2, 0, 2],
                      },
                    ]),
                  ],
                },
                layout: 'noBorders',
                margin: [0, 0, 0, 6],
              },
              {
                table: {
                  widths: ['*'],
                  body: [
                    [
                      {
                        fillColor: COLORS.ink,
                        margin: [12, 10, 12, 10],
                        columns: [
                          { text: totalLabel, fontSize: 9, bold: true, color: COLORS.totalLabel, margin: [0, 4, 0, 0] },
                          {
                            text: `${formatAmount(total)} ${currency}`,
                            fontSize: 16,
                            bold: true,
                            color: '#FFFFFF',
                            alignment: 'right',
                          },
                        ],
                      },
                    ],
                  ],
                },
                layout: 'noBorders',
              },
            ],
          },
        ],
        columnGap: 16,
        margin: [0, 0, 0, 14],
      },

      // Terms & conditions
      {
        ...card('TERMS & CONDITIONS', [
          { ol: TERMS, fontSize: 8, color: COLORS.slate600, lineHeight: 1.2, margin: [0, 1, 0, 0] },
        ]),
        margin: [0, 0, 0, 18],
      },

      // Authorized signature
      {
        columns: [
          { width: '*', text: '' },
          {
            width: 160,
            stack: [
              { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 160, y2: 0, lineWidth: 0.75, lineColor: COLORS.faint }] },
              {
                text: 'Authorized Signature',
                fontSize: 8,
                bold: true,
                color: COLORS.slate800,
                alignment: 'center',
                margin: [0, 4, 0, 0],
              },
              { text: signatureName || '', fontSize: 8, color: COLORS.muted, alignment: 'center', margin: [0, 1, 0, 0] },
            ],
          },
        ],
        margin: [0, 0, 0, 14],
        unbreakable: true,
      },

      // Support banner
      {
        table: {
          widths: [3, '*'],
          body: [
            [
              { fillColor: COLORS.accent, text: '' },
              {
                fillColor: COLORS.panel,
                margin: [10, 7, 10, 7],
                stack: [
                  { text: supportText, fontSize: 8.5, bold: true, color: COLORS.slate800 },
                  {
                    text: 'Support: +971 50 123 4567   |   Email: support@fleetnexis.com   |   Web: www.fleetnexis.com',
                    fontSize: 8,
                    color: COLORS.muted,
                    margin: [0, 2, 0, 0],
                  },
                ],
              },
            ],
          ],
        },
        layout: 'noBorders',
      },
    ],
    styles: {
      tableHeader: {
        fontSize: 8.5,
        bold: true,
        color: '#FFFFFF',
        fillColor: COLORS.ink,
        margin: [0, 3, 0, 3],
      },
      tableCell: {
        fontSize: 9,
        color: COLORS.slate800,
      },
    },
    defaultStyle: {
      font: 'Tajawal',
      fontSize: 9,
    },
  };
};

// pdfMake in the browser needs images as data URLs. Only an actual image is
// accepted: a missing file comes back as index.html (SPA fallback), which
// pdfMake would reject and the whole document would fail.
const loadImageAsDataUrl = async (src) => {
  if (!src || src.startsWith('data:image/')) return src || null;
  try {
    const res = await fetch(src);
    const blob = await res.blob();
    if (!res.ok || !blob.type.startsWith('image/')) return null;
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.error('Unable to load FleetNexis logo for PDF', err);
    return null;
  }
};

let logoDataUrl = null;
const loadLogo = async () => {
  if (!logoDataUrl) logoDataUrl = await loadImageAsDataUrl(fleetnexisLogo);
  return logoDataUrl;
};

/**
 * Builds and opens a PDF in a new tab. `buildDefinition(logo)` returns the
 * pdfMake document definition (logo is a data URL, or null if unavailable).
 *
 * Fonts and vfs are passed to createPdf directly instead of being set on the
 * shared window.pdfMake, so other PDFs in the panel can't change them.
 */
export const openBrandedPdf = async (buildDefinition) => {
  const [{ default: pdfMake }, { default: Tajawal }, { default: TajawalBold }, logo] = await Promise.all([
    import('pdfmake'),
    import('../../../assets/fonts/Tajawal'),
    import('../../../assets/fonts/TajawalBold'),
    loadLogo(),
  ]);
  const vfs = { 'Tajawal.ttf': Tajawal.font, 'Tajawal-Bold.ttf': TajawalBold };
  const fonts = {
    Tajawal: {
      normal: 'Tajawal.ttf',
      bold: 'Tajawal-Bold.ttf',
      italics: 'Tajawal.ttf',
      bolditalics: 'Tajawal-Bold.ttf',
    },
  };
  pdfMake.createPdf(buildDefinition(logo), undefined, fonts, vfs).open();
};
